import { createElement, memo, forwardRef } from 'react';
import { SVG } from '../SVG';
import { IconProps } from './types';
import { useDesignTokens, DesignTokens, renderInlineCssVars } from '../../theme';

import cx from 'classnames';
import cn from './Icon.css.json';
import './Icon.css';

export const Icon = memo(
  forwardRef<HTMLSpanElement, IconProps>(function Icon(props, ref) {
    const dt = useDesignTokens();
    let dtFg = props.tokens ? props.tokens().fg : undefined;

    if (props.color) {
      switch (props.color) {
        case 'primary':
          dtFg = dt.enableNovaTokens ? dt.c.colorPrimary : dt.c.primary;
          break;
        case 'secondary':
          dtFg = dt.enableNovaTokens ? dt.c.colorSecondary : dt.c.secondary;
          break;
        case 'alert':
          dtFg = dt.enableNovaTokens ? dt.c.colorAlert : dt.c.alert;
          break;
        case 'warning':
          dtFg = dt.enableNovaTokens ? dt.c.colorWarning : dt.c.warning;
          break;
        case 'success':
          dtFg = dt.enableNovaTokens ? dt.c.colorSuccess : dt.c.success;
          break;
        case 'informational':
          dtFg = dt.enableNovaTokens ? dt.c.colorInfo : dt.c.informational;
          break;
        case 'neutral':
          dtFg = dt.enableNovaTokens ? dt.c.colorNeutral : dt.c.neutral;
          break;
        default:
          dtFg = undefined;
      }
    }

    const styleTokenValues = iconThemeTokens(dt);
    const overrideTokens = props.tokens?.();
    const style = renderInlineCssVars(
      {
        'default-override-fg':
          dtFg ?? overrideTokens?.['default-fg'] ?? overrideTokens?.fg ?? undefined,
        'neutral-override-fg':
          dtFg ?? overrideTokens?.['neutral-fg'] ?? overrideTokens?.fg ?? undefined,
        'alert-override-fg':
          dtFg ?? overrideTokens?.['alert-fg'] ?? overrideTokens?.fg ?? undefined,
        'warning-override-fg':
          dtFg ?? overrideTokens?.['warning-fg'] ?? overrideTokens?.fg ?? undefined,
        'success-override-fg':
          dtFg ?? overrideTokens?.['success-fg'] ?? overrideTokens?.fg ?? undefined,
        'information-override-fg':
          dtFg ?? overrideTokens?.['information-fg'] ?? overrideTokens?.fg ?? undefined,
        ...styleTokenValues,
      },
      'icon'
    );

    return (
      <SVG
        id={props.id}
        ref={ref}
        style={{ ...style, ...props.style }}
        icon={props.icon}
        className={cx(cn.icon, props.className, props.disabled && cn.disabled)}
        testId={props.testId}
        type="icon"
        role={props.role}
        onClick={(e) => {
          if (props.disabled) {
            e.stopPropagation();
            return;
          }
          props.onClick?.(e);
          props.onPress?.();
        }}
        onKeyDown={(e) => {
          if (props.disabled) return;
          if (e.key === ' ' || e.key === 'Enter') {
            props.onPress?.();
          }
        }}
        onDoubleClick={(e) => {
          if (props.disabled) return;
          props.onDoubleClick?.(e);
        }}
        ariaHidden={props.ariaHidden}
        tabIndex={props.tabIndex ?? (props.onPress ? 0 : undefined)}
        ariaLabel={props.ariaLabel}
        tooltip={props.tooltip}
      />
    );
  })
);

function iconThemeTokens(dt: DesignTokens) {
  if (dt.enableNovaTokens) {
    return { opacity: 0.3 };
  }

  switch (dt.themeVariant) {
    case 'dark':
      return { opacity: 0.25 };
    case 'high-contrast':
      return { opacity: 0.5 };
    default:
      return { opacity: 0.3 };
  }
}
